import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Building2, Plus, Edit, Eye, Ban, Play, LogIn, Loader2, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : n >= 999_000 ? "Ilimitado" : n.toLocaleString("pt-BR");

const statusVariant: Record<string, "default" | "secondary" | "destructive"> = {
  active: "default", suspended: "destructive", cancelled: "secondary",
};
const statusLabel: Record<string, string> = {
  active: "Ativo", suspended: "Suspenso", cancelled: "Cancelado",
};

export function TenantsTab() {
  const { data: tenants, isLoading, refetch } = trpc.superAdmin.listTenants.useQuery();
  const { data: dbPlans } = trpc.superAdmin.listPlans.useQuery();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "suspended" | "cancelled">("all");
  const [planFilter, setPlanFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    let r = tenants ?? [];
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(t => t.name.toLowerCase().includes(s) || t.ownerEmail.toLowerCase().includes(s));
    }
    if (statusFilter !== "all") r = r.filter(t => t.status === statusFilter);
    if (planFilter !== "all") r = r.filter(t => t.plan?.key === planFilter);
    return r;
  }, [tenants, search, statusFilter, planFilter]);

  // Mutations
  const suspendMut = trpc.superAdmin.suspendTenant.useMutation({ onSuccess: () => { toast.success("Empresa suspensa"); refetch(); } });
  const reactivateMut = trpc.superAdmin.reactivateTenant.useMutation({ onSuccess: () => { toast.success("Empresa reativada"); refetch(); } });
  const deleteMut = trpc.superAdmin.deleteTenant.useMutation({ onSuccess: () => { toast.success("Empresa removida"); refetch(); } });
  const updatePlanMut = trpc.superAdmin.updatePlan.useMutation({
    onSuccess: () => { toast.success("Plano alterado"); refetch(); setPlanEdit(null); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const createMut = trpc.superAdmin.createTenant.useMutation({
    onSuccess: () => { toast.success("Empresa criada"); refetch(); setShowCreate(false); resetCreate(); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const impersonateMut = trpc.superAdmin.impersonate.useMutation({
    onSuccess: () => { toast.success("Sessão criada — redirecionando…"); window.location.href = "/"; },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  // Create dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [c, setC] = useState({ name: "", ownerName: "", ownerEmail: "", ownerPassword: "", planKey: "free" });
  const resetCreate = () => setC({ name: "", ownerName: "", ownerEmail: "", ownerPassword: "", planKey: "free" });

  // Plan edit dialog. Carrega também como as MENSAGENS são cobradas: quem
  // paga a Meta (o cliente direto ou nós, com margem) é decisão comercial
  // nossa e mora no mesmo lugar em que se muda o plano.
  const [planEdit, setPlanEdit] = useState<{
    tenantId: number; tenantName: string; planId: number | null;
    modo: "meta_direct" | "reseller"; markupPercent: number;
  } | null>(null);
  const setBillingMut = trpc.superAdmin.setWhatsappBilling.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  // Detail dialog
  const [detail, setDetail] = useState<any>(null);
  const { data: detailUsers } = trpc.superAdmin.tenantUsers.useQuery(
    { tenantId: detail?.id ?? 0 }, { enabled: !!detail },
  );

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Empresas</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {tenants?.length ?? 0} cadastradas · {tenants?.filter(t => t.status === "active").length ?? 0} ativas
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1.5" /> Nova Empresa
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Buscar por nome ou e-mail…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />
        <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="suspended">Suspensos</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos planos</SelectItem>
            {(dbPlans ?? []).map(p => <SelectItem key={p.id} value={p.key}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-16 text-center">
          <Building2 className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Nenhuma empresa encontrada.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(t => {
            const cPct = t.maxContacts > 0 && t.maxContacts < 999_000 ? Math.min(Math.round(((t.contactsCount ?? 0) / t.maxContacts) * 100), 100) : 0;
            const mPct = t.maxMessagesPerMonth > 0 && t.maxMessagesPerMonth < 999_000 ? Math.min(Math.round(((t.messagesThisMonth ?? 0) / t.maxMessagesPerMonth) * 100), 100) : 0;
            return (
              <Card key={t.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-base font-bold text-muted-foreground shrink-0">
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-[15px]">{t.name}</h3>
                        {t.plan && <Badge variant="outline" className="text-[10px]">{t.plan.name}</Badge>}
                        <Badge variant={statusVariant[t.status] ?? "secondary"} className="text-[10px]">{statusLabel[t.status]}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.ownerEmail} · desde {new Date(t.createdAt).toLocaleDateString("pt-BR")}</p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Contatos</p>
                          <p className="text-sm font-semibold">{fmt(t.contactsCount)} <span className="text-[10px] font-normal text-muted-foreground">/ {fmt(t.maxContacts)}</span></p>
                          <div className="w-full h-1 bg-muted rounded-full mt-1.5">
                            <div className={`h-full rounded-full ${cPct > 90 ? "bg-red-500" : cPct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${cPct}%` }} />
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Msgs/mês</p>
                          <p className="text-sm font-semibold">{fmt(t.messagesThisMonth)} <span className="text-[10px] font-normal text-muted-foreground">/ {fmt(t.maxMessagesPerMonth)}</span></p>
                          <div className="w-full h-1 bg-muted rounded-full mt-1.5">
                            <div className={`h-full rounded-full ${mPct > 90 ? "bg-red-500" : mPct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${mPct}%` }} />
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Usuários</p>
                          <p className="text-sm font-semibold">até {fmt(t.maxUsers)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Fluxos</p>
                          <p className="text-sm font-semibold">até {fmt(t.maxFlows)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setPlanEdit({
                        tenantId: t.id, tenantName: t.name, planId: t.plan?.id ?? null,
                        modo: t.whatsappBillingMode === "reseller" ? "reseller" : "meta_direct",
                        markupPercent: Number(t.whatsappMarkupPercent ?? 0) || 0,
                      })}>
                        <Edit className="w-3 h-3 mr-1.5" /> Plano
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setDetail(t)}>
                        <Eye className="w-3 h-3 mr-1.5" /> Detalhes
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => {
                        if (confirm(`Impersonar owner de "${t.name}"? Sua sessão atual será substituída.`)) impersonateMut.mutate({ tenantId: t.id });
                      }}>
                        <LogIn className="w-3 h-3 mr-1.5" /> Impersonar
                      </Button>
                      {t.status === "active" && (
                        <Button size="sm" variant="outline" className="text-xs h-7 text-red-600 border-red-200"
                          onClick={() => { if (confirm(`Suspender "${t.name}"?`)) suspendMut.mutate({ tenantId: t.id }); }}>
                          <Ban className="w-3 h-3 mr-1.5" /> Suspender
                        </Button>
                      )}
                      {t.status === "suspended" && (
                        <Button size="sm" variant="outline" className="text-xs h-7 text-emerald-700"
                          onClick={() => reactivateMut.mutate({ tenantId: t.id })}>
                          <Play className="w-3 h-3 mr-1.5" /> Reativar
                        </Button>
                      )}
                      {t.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" className="text-xs h-7 text-red-600"
                          onClick={() => {
                            const conf = prompt(`Para confirmar a remoção de "${t.name}", digite o nome:`);
                            if (conf === t.name) deleteMut.mutate({ tenantId: t.id });
                            else if (conf != null) toast.error("Nome não confere — operação cancelada.");
                          }}>
                          <Trash2 className="w-3 h-3 mr-1.5" /> Remover
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nova Empresa</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-xs">Nome da Empresa *</Label><Input value={c.name} onChange={e => setC({ ...c, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Nome do Owner *</Label><Input value={c.ownerName} onChange={e => setC({ ...c, ownerName: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">E-mail do Owner *</Label><Input type="email" value={c.ownerEmail} onChange={e => setC({ ...c, ownerEmail: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Senha *</Label><Input type="password" value={c.ownerPassword} onChange={e => setC({ ...c, ownerPassword: e.target.value })} placeholder="Min. 8 chars" /></div>
              <div className="space-y-1">
                <Label className="text-xs">Plano</Label>
                <Select value={c.planKey} onValueChange={v => setC({ ...c, planKey: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(dbPlans ?? []).map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={() => createMut.mutate(c)} disabled={createMut.isPending || !c.name || !c.ownerName || !c.ownerEmail || c.ownerPassword.length < 8}>
              {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan edit */}
      <Dialog open={!!planEdit} onOpenChange={(o) => { if (!o) setPlanEdit(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Plano e cobrança — {planEdit?.tenantName}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {(dbPlans ?? []).map(p => {
              const sel = planEdit?.planId === p.id;
              return (
                <button key={p.id} onClick={() => setPlanEdit(prev => prev ? { ...prev, planId: p.id } : prev)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${sel ? "border-primary bg-primary/5" : "border-muted hover:border-primary/30"}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-sm">{p.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{p.price}</span>
                    </div>
                    {sel && <Badge className="text-[10px]">Selecionado</Badge>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{fmt(p.maxContacts)} contatos · {fmt(p.maxMessagesPerMonth)} msgs · {fmt(p.maxUsers)} users · {fmt(p.maxFlows)} fluxos</p>
                </button>
              );
            })}
          </div>
          {planEdit && (
            <div className="space-y-2 border-t pt-3">
              <Label className="text-xs">Mensagens do WhatsApp</Label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { v: "meta_direct" as const, t: "Cliente paga a Meta", d: "Painel informativo, sem margem." },
                  { v: "reseller" as const, t: "Nós faturamos", d: "Custo Meta + margem." },
                ]).map(o => (
                  <button key={o.v} onClick={() => setPlanEdit(prev => prev ? { ...prev, modo: o.v } : prev)}
                    className={`text-left p-2 rounded-lg border-2 transition-all ${planEdit.modo === o.v ? "border-primary bg-primary/5" : "border-muted hover:border-primary/30"}`}>
                    <p className="text-xs font-semibold">{o.t}</p>
                    <p className="text-[11px] text-muted-foreground">{o.d}</p>
                  </button>
                ))}
              </div>
              {planEdit.modo === "reseller" && (
                <div className="space-y-1">
                  <Label className="text-xs">Margem (%)</Label>
                  <Input type="number" min={0} max={200} value={planEdit.markupPercent}
                    onChange={e => setPlanEdit(prev => prev ? { ...prev, markupPercent: Number(e.target.value) } : prev)} />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanEdit(null)}>Cancelar</Button>
            <Button onClick={async () => {
              if (!planEdit?.planId) return;
              // A cobrança vai primeiro: se ela falhar, o plano não muda e a
              // tela não fecha dizendo que deu tudo certo. O try/catch é o que
              // impede a rejeição de virar unhandled — o `onError` mostra o
              // toast, mas a promise continua rejeitando sem ele.
              try {
                await setBillingMut.mutateAsync({
                  tenantId: planEdit.tenantId,
                  modo: planEdit.modo,
                  markupPercent: Number.isFinite(planEdit.markupPercent) ? planEdit.markupPercent : 0,
                });
              } catch {
                return;
              }
              updatePlanMut.mutate({ tenantId: planEdit.tenantId, planId: planEdit.planId });
            }}
              disabled={updatePlanMut.isPending || setBillingMut.isPending || !planEdit?.planId}>
              {updatePlanMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" /> {detail?.name}
            {detail && <Badge variant={statusVariant[detail.status]} className="text-[10px] ml-1">{statusLabel[detail.status]}</Badge>}
          </DialogTitle></DialogHeader>
          {detail && <DetailContent tenant={detail} users={detailUsers} onRefresh={refetch} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailContent({ tenant, users, onRefresh }: { tenant: any; users: any; onRefresh: () => void }) {
  const updateMut = trpc.superAdmin.updateTenantDetails.useMutation({
    onSuccess: () => { toast.success("Dados atualizados"); onRefresh(); setEditing(false); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: tenant.name || "",
    ownerEmail: tenant.ownerEmail || "",
    cnpj: tenant.cnpj || "",
    phone: tenant.phone || "",
    address: tenant.address || "",
    city: tenant.city || "",
    state: tenant.state || "",
    zipCode: tenant.zipCode || "",
    billingEmail: tenant.billingEmail || "",
    paymentMethod: tenant.paymentMethod || "",
    paymentNotes: tenant.paymentNotes || "",
  });
  const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  const save = () => {
    const cleaned = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
    updateMut.mutate({ tenantId: tenant.id, ...cleaned } as any);
  };

  return (
    <div className="space-y-5 py-2">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Cadastro</h3>
          {!editing && <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setEditing(true)}><Edit className="w-3 h-3 mr-1" /> Editar</Button>}
        </div>
        {editing ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">E-mail Owner</Label><Input value={form.ownerEmail} onChange={e => setForm({ ...form, ownerEmail: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">CNPJ</Label><Input value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Telefone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2 space-y-1"><Label className="text-xs">Endereço</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Cidade</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">UF</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} maxLength={2} /></div>
              <div className="space-y-1"><Label className="text-xs">CEP</Label><Input value={form.zipCode} onChange={e => setForm({ ...form, zipCode: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">E-mail cobrança</Label><Input value={form.billingEmail} onChange={e => setForm({ ...form, billingEmail: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Método pagto</Label><Input value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })} placeholder="stripe / pix / boleto / manual" /></div>
            <div className="col-span-2 space-y-1"><Label className="text-xs">Observações</Label><Input value={form.paymentNotes} onChange={e => setForm({ ...form, paymentNotes: e.target.value })} /></div>
            <div className="col-span-2 flex gap-2 mt-1">
              <Button size="sm" onClick={save} disabled={updateMut.isPending}>
                {updateMut.isPending && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Salvar
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div><span className="text-muted-foreground">Nome:</span> <span className="font-medium">{tenant.name}</span></div>
            <div><span className="text-muted-foreground">Owner:</span> <span className="font-medium">{tenant.ownerEmail}</span></div>
            <div><span className="text-muted-foreground">CNPJ:</span> <span className="font-medium">{tenant.cnpj || "—"}</span></div>
            <div><span className="text-muted-foreground">Telefone:</span> <span className="font-medium">{tenant.phone || "—"}</span></div>
            <div className="col-span-2"><span className="text-muted-foreground">Endereço:</span> <span className="font-medium">{[tenant.address, tenant.city, tenant.state, tenant.zipCode].filter(Boolean).join(", ") || "—"}</span></div>
            <div><span className="text-muted-foreground">Slug:</span> <span className="font-mono text-xs">{tenant.slug}</span></div>
            <div><span className="text-muted-foreground">Criado em:</span> <span className="font-medium">{fmtDate(tenant.createdAt)}</span></div>
          </div>
        )}
      </section>

      <hr />

      <section>
        <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Plano e Uso</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <div><span className="text-muted-foreground">Plano:</span> <span className="font-medium">{tenant.plan?.name ?? "Sem plano"}</span></div>
          <div><span className="text-muted-foreground">Preço:</span> <span className="font-medium">{tenant.plan?.price ?? "—"}</span></div>
          <div><span className="text-muted-foreground">Contatos:</span> <span className="font-medium">{fmt(tenant.contactsCount)} / {fmt(tenant.maxContacts)}</span></div>
          <div><span className="text-muted-foreground">Msgs/mês:</span> <span className="font-medium">{fmt(tenant.messagesThisMonth)} / {fmt(tenant.maxMessagesPerMonth)}</span></div>
        </div>
      </section>

      <hr />

      <section>
        <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Pagamento</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <div><span className="text-muted-foreground">Método:</span> <span className="font-medium">{tenant.paymentMethod || "—"}</span></div>
          <div><span className="text-muted-foreground">E-mail cobrança:</span> <span className="font-medium">{tenant.billingEmail || "—"}</span></div>
          <div><span className="text-muted-foreground">Último pagto:</span> <span className="font-medium">{fmtDate(tenant.lastPaymentAt)}</span></div>
          <div><span className="text-muted-foreground">Próxima cobrança:</span> <span className="font-medium">{fmtDate(tenant.nextBillingAt)}</span></div>
          {tenant.paymentNotes && <div className="col-span-2"><span className="text-muted-foreground">Obs:</span> <span className="font-medium">{tenant.paymentNotes}</span></div>}
          {/* Aviso automático do Stripe. Separado da observação acima, que é
              escrita à mão e não pode ser apagada por webhook. */}
          {tenant.billingAlert && <div className="col-span-2 text-amber-700 dark:text-amber-400"><span className="text-muted-foreground">Cobrança:</span> <span className="font-medium">{tenant.billingAlert}</span></div>}
        </div>
      </section>

      <hr />

      <section>
        <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Usuários ({users?.length ?? 0})</h3>
        {users?.length ? (
          <div className="space-y-1.5">
            {users.map((u: any) => (
              <div key={u.id} className="flex items-center justify-between py-1 text-sm">
                <div><span className="font-medium">{u.name}</span> <span className="text-muted-foreground ml-2">{u.email}</span></div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px]">{u.tenantRole}</Badge>
                  <Badge variant={u.isActive ? "default" : "secondary"} className="text-[10px]">{u.isActive ? "Ativo" : "Inativo"}</Badge>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">Nenhum usuário</p>}
      </section>
    </div>
  );
}
